import React, { Fragment, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { bindActionCreators } from 'redux'

import { Avatar, Box, Flex, Image, SkeletonCircle, SkeletonText } from '@chakra-ui/react'
import DsrMessageOptionsButton from '../messages/DsrMessageOptionsButton'
import DsrMessageToolbar from '../messages/DsrMessageToolbar'
import DsrRepliesDisplayModule from '../messages/DsrRepliesDisplayModule'
import DsrCreateContentPostModule from './DsrCreateContentPostModule'

// Images
import DsrStartIcon from '../styles/images/dsr-start-icon.png'
import { getUserDetailsUsingDsrMembersArray } from '../../profile/helpers/user-helper'
import { generateRandomString, getThisUserIdToken, isValidArray, openInNewTab } from '../../global/helpers/global-helpers'
import { determineIsPostAuthor } from '../helpers/dsr-feed-helpers'
import { displayReactIcon, getUrlInPostContent } from "../helpers/dsr-display-helpers";
import { ReactTinyLink } from 'react-tiny-link'
import '../styles/dsr-display.scss'

// Redux
import {
  replaceDsrFeedContent,
  resetCreateDsrReply,
  resetCreateNewDsrFeedPost,
  resetDeleteDsrFeedPost,
  resetDeleteDsrReply,
  resetEditDsrFeedPost,
  resetEditDsrReaction,
  resetEditDsrReply
} from '../../digital-sales-room/actions/dsr-actions'
import { LoomVideoDisplay } from './LoomVideoDisplay'
import LoaderCentered from "../../global/components/helper-components/components/LoaderCentered";
import { Mention, MentionsInput } from "react-mentions";
import { mentionStyle } from "../../forms/TextareaField/components/InputTextAreaWithUserTagging";
import config from "../../../config/app-config";
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton'
import LaunchdeckImageCarousel from '../../global/components/helper-components/components/LaunchdeckImageCarousel'
import FeedPostButtonModule from '../messages/FeedPostButtonModule';
import LaunchdeckTextLoader from '../../global/components/helper-components/components/LaunchdeckTextLoader'
import NewFeedPostsLoadingDisplay from './NewFeedPostsLoadingDisplay'



function DisplayNewsFeed({
    dsrId,
    dsrTitle,
    userIdToken,
    displayedPostContent,
    loadMoreFeedContent,
    showLoadMore,
    loadingMoreContent,
    userProfile,
    dsrTeamMembers,
    handleCreateNewPost,
    handleAddRemoveMessageReaction,
    onClickDeleteMessageOption,
    onClickDeleteReplyOption,
    handleCreateNewPostReply,
    handleOpenNewPostModal,
    handleSelectSubpage,
    handleStopDsrModalLoader,
    handleChangeUserActionRequired,
    // Refresh parent component data state
    refreshData,
    // Redux States
    createNewDsrFeedPostOutcome,
    editDsrFeedPostOutcome,
    deleteDsrFeedPostOutcome,
    createNewDsrFeedReplyOutcome,
    editDsrFeedReplyOutcome,
    deleteDsrFeedReplyOutcome,
    editDsrReactionOutcome,
    dsrFeedContent,
    isMobileView,
    isDsrAdmin,
    // Post management
    resetCreateNewDsrFeedPost,
    resetEditDsrFeedPost,
    resetDeleteDsrFeedPost,

    // Reply management
    resetCreateDsrReply,
    resetEditDsrReply,
    resetDeleteDsrReply,

    // Reaction management
    resetEditDsrReaction,
    replaceDsrFeedContent,

    // UX management
    processingDsrPosts,
    processingNewDsrPosts
  }) {
  
  const { corsProxy, acceptedImageFileTypes } = config;
  const currentUserId = getThisUserIdToken();
  const [thisDsrTeamMembers, setAllDsrTeamMembers] = useState(null);

  const [showPostOptionButton, setShowPostOptionButton] = useState(null);
  const [showOptionButtonPostId, setShowOptionButtonPostId] = useState(null);


  // Auto scroll to top
  const scrollToRef = useRef(false);

  useEffect(() => {
    if (scrollToRef.current) {
      const elementPosition = scrollToRef.current.getBoundingClientRect().top;
      const offset = 200; // Offset (200px)

      window.scrollTo({
        top: elementPosition - offset, // Scroll to the position minus the offset
        // behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (dsrTeamMembers) {
      const { sellerTeam, buyerTeam } = dsrTeamMembers || {};
      const allMembersArray = (sellerTeam && buyerTeam) ? [...sellerTeam, ...buyerTeam] : [...sellerTeam]
      setAllDsrTeamMembers(allMembersArray)
    }

  }, [dsrTeamMembers]);

  // UX management: render new content when user action is taken
  useEffect(() => {
    if (createNewDsrFeedPostOutcome) {
      const { updatedSuccessfully, newPostCreated } = createNewDsrFeedPostOutcome || {};

      if (updatedSuccessfully === true && dsrFeedContent) {
        replaceDsrFeedContent([...dsrFeedContent, newPostCreated])
      }
      handleStopDsrModalLoader()
      resetCreateNewDsrFeedPost()
    }
  }, [createNewDsrFeedPostOutcome, replaceDsrFeedContent, resetCreateNewDsrFeedPost, displayedPostContent, refreshData, dsrFeedContent, handleStopDsrModalLoader]);

  //  UX management for editing Feed Post Content (only text ones)
  useEffect(() => {
    if (editDsrFeedPostOutcome) {
      const { updatedSuccessfully, updatedPostId, updatedPostType, updatedPostContent, updatedPostImages } = editDsrFeedPostOutcome || {};
      let updatedDisplayedFeedPosts = displayedPostContent;
      
      if (updatedSuccessfully === true) { // TODO make sure that it will not mutate state
        for (const post of updatedDisplayedFeedPosts) {
          if (post.postId === updatedPostId && updatedPostContent) {
            if (updatedPostType === 'short-text') {
              post.postType = updatedPostType
              post.postContent = updatedPostContent

              delete post.images;
              break;
            } else if (updatedPostType === 'images') {
              post.postType = updatedPostType
              post.postContent = updatedPostContent
              
              if (isValidArray(updatedPostImages)) {
                post.images = updatedPostImages
              } else {
                delete post.images;
              }
            
              break;
            }
          }
        }
        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetEditDsrFeedPost()
    }
  }, [editDsrFeedPostOutcome, resetEditDsrFeedPost, displayedPostContent, refreshData]);

  // UX management to update deleted post outcome
  useEffect(() => {
    if (deleteDsrFeedPostOutcome) {
      const { updatedSuccessfully, deletedPostId } = deleteDsrFeedPostOutcome || {};
      let updatedDisplayedFeedPosts = displayedPostContent;

      if (updatedSuccessfully === true) { // TODO make sure that it will not mutate state
        for (const post of updatedDisplayedFeedPosts) {
          if (post.postId === deletedPostId) {
            post.postStatus = 'deleted';
            post.postDeletedAt = new Date()
            break;
          }

        }
        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetDeleteDsrFeedPost()
    }
  }, [deleteDsrFeedPostOutcome, resetDeleteDsrFeedPost, displayedPostContent, refreshData]);

  // UX management to display newly created replies
  useEffect(() => {
    if (createNewDsrFeedReplyOutcome) {
      const { updatedSuccessfully, updatedPostId, updatedPostReplies } = createNewDsrFeedReplyOutcome || {};

      if (updatedSuccessfully === true) {
        const updatedDisplayedFeedPosts = displayedPostContent.map(post => post.postId === updatedPostId ? { ...post, postReplies: updatedPostReplies } : post);

        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetCreateDsrReply()
    }
  }, [createNewDsrFeedReplyOutcome, resetCreateDsrReply, displayedPostContent, refreshData]);

  //  UX management to display updated replies (edited reply by author).
  useEffect(() => {
    if (editDsrFeedReplyOutcome) {
      const { updatedSuccessfully, updatedPostId, updatedReplyId, updatedReplyContent, replyUpdatedAt } = editDsrFeedReplyOutcome || {};

      if (updatedSuccessfully === true) { // TODO make sure that it will not mutate state
        let updatedDisplayedFeedPosts = displayedPostContent;

        for (const post of updatedDisplayedFeedPosts) {
          if (post.postId === updatedPostId) {
            const { postReplies } = post || {};

            for (const reply of postReplies) {
              if (reply.replyId === updatedReplyId) {
                reply.replyContent = updatedReplyContent
                reply.replyUpdatedAt = replyUpdatedAt
                break;
              }
            }
            break;
          }
        }

        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetEditDsrReply()
    }
  }, [editDsrFeedReplyOutcome, resetEditDsrReply, displayedPostContent, refreshData]);

  // UX management to display updated replies (deleted reply by author)
  useEffect(() => {
    if (deleteDsrFeedReplyOutcome) {
      const { updatedSuccessfully, updatedPostId, deletedReplyId } = deleteDsrFeedReplyOutcome || {};

      if (updatedSuccessfully === true) {
        let updatedDisplayedFeedPosts = displayedPostContent;

        for (const post of updatedDisplayedFeedPosts) { // TODO make sure that it will not mutate state
          if (post.postId === updatedPostId) {
            const { postReplies } = post || {};

            for (const reply of postReplies) {
              if (reply.replyId === deletedReplyId) {
                reply.replyStatus = 'deleted'
              }
            }
          }
        }

        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetDeleteDsrReply()
    }
  }, [deleteDsrFeedReplyOutcome, resetDeleteDsrReply, displayedPostContent, refreshData]);

  // UX management to display updated post reactions
  useEffect(() => {
    if (editDsrReactionOutcome) {
      const { updatedSuccessfully, updatedPostId, updatedPostReactions } = editDsrReactionOutcome || {};

      if (updatedSuccessfully === true) { // TODO make sure that it will not mutate state
        let updatedDisplayedFeedPosts = displayedPostContent;

        for (const post of updatedDisplayedFeedPosts) {
          if (post.postId === updatedPostId) {

            post.postReactions = updatedPostReactions
            break;
          }
        }

        refreshData('updated-news-feed-content', updatedDisplayedFeedPosts)
      }

      resetEditDsrReaction()
    }
  }, [editDsrReactionOutcome, resetEditDsrReaction, displayedPostContent, refreshData]);


  const handleShowPostOptionsButton = (postId) => {
    setShowPostOptionButton(postId ? true : false)
    setShowOptionButtonPostId(postId)
  }

  const renderPostLoadingComponent = (key, postId) => {
    return (
      <Box
      key={key ? key : generateRandomString(6)}
      w='100%'
      mt='1em' mb='2em' p='1em'
      bg='white'
      rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)'
    >
      <Flex width='100%' mb='2em' justify='space-between' align='flex-start'>
        <Flex justify='flex-start' gap='1em'>
          <SkeletonCircle size='3em' />
          <Box w='35%' minW='16em'>
            <SkeletonText mt='3' noOfLines={2} spacing='4' skeletonHeight='0.875em' rounded='0.25em'/>
          </Box>
        </Flex>
        <Flex justify='flex-end' pr='0.5em'>
           <LaunchdeckTextLoader copyText="Updating post" color='gray.500' fontSize='0.7em' postId={postId}/>
        </Flex>
      </Flex>

      <SkeletonText px='0.5em' pb='0.5em' noOfLines={3} spacing='4' skeletonHeight='0.875em' />
    </Box>
    )
  }

  // Messages
  const displayedMessages = displayedPostContent && displayedPostContent.sort((a, b) => new Date(b.postCreatedAt) - new Date(a.postCreatedAt)).map((message, messageIndex) => {

    const { postType, postId, postStatus, authorUserId, postContent, postCreatedAt,
      postReactions, postUpdatedAt, postReplies, images, video, loomVideo, flaggedPostReports } = message || {};
    
    const isProcessingState = processingDsrPosts.includes(postId);
    const activeReplies = (isValidArray(postReplies)) ? postReplies.filter(reply => reply.replyStatus === "active") : null

    const messageTimestamp = moment(postCreatedAt).format('lll')
    const isEdited = postUpdatedAt ? true : false;
    const authorProfile = getUserDetailsUsingDsrMembersArray(dsrTeamMembers, authorUserId);
    const { memberName, memberProfilePicSrc } = authorProfile || {};

    const replyCount = (activeReplies) ? activeReplies.length : 0;
    const hasReportedThisPost = isValidArray(flaggedPostReports) ? flaggedPostReports.some(obj => obj.reportUserId === currentUserId) : false;

    // Determine whether current user is the post author
    const isPostAuthor = authorProfile ? determineIsPostAuthor(authorProfile.userId, currentUserId) : null;

    if (postStatus === 'deleted') {
      return null
    }

    if (hasReportedThisPost === true) {
      return (<Box key={`message_${messageIndex}`} mb='2em' color='gray.500'>
        <Flex justify='flex-start' align='center' bg='white' border='1px solid' borderColor='gray.300' rounded='0.5em' p='0.2em 1.25em' gap='0.5em'>
          <Box color='brandPink.500'>{displayReactIcon("alert", "1em")}</Box>
          <Box p='0.5em' fontSize='0.8em'>This post has been reported & hidden </Box>
        </Flex>
      </Box>
      )
    }

    const renderPostTextOrMedia = (content) => {
      if (isProcessingState) {
        const key = `message_${messageIndex}`
        return renderPostLoadingComponent(key, postId)
      }

      return (
        <Box
          key={`message_${messageIndex}`}
          className='fade-in'
          w='100%'
          mt='1em' mb='2em' p='1em'
          bg='white'
          rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)'
          onMouseOver={() => { handleShowPostOptionsButton(postId) }}
          onMouseOut={() => { handleShowPostOptionsButton("") }}
        >
          <Flex width='100%' justify='space-between'>
            <Box>
              <Flex justify='flex-start' align='center'>
                <Avatar h='4.2em' w='4.2em' name={memberName} bg='gray.100' color='gray.700' src={memberProfilePicSrc} />
                <Box pl='1em'>
                  <Flex justify='flex-start' fontSize='0.9em'>
                    <Box fontWeight='semibold'>{memberName}</Box>
                    <Box pl='0.4em'>posted {postType === "shared-url" && "a link"}</Box>
                  </Flex>
                  <Box fontSize='0.8em' color='gray.500'>{messageTimestamp} {isEdited ?
                    <Box as='span' fontSize='1em' color='gray.500' lineHeight='1em'> • Edited</Box> : null}</Box>
                </Box>

              </Flex>
            </Box>


            <FeedPostButtonModule
              allowSliding={showPostOptionButton && showOptionButtonPostId === postId ? true : false}
              flaggedPostReports={flaggedPostReports}
              isDsrAdmin={isDsrAdmin}
              dsrTeamMembers={dsrTeamMembers}
            >
              <DsrMessageOptionsButton
                onClickDeleteMessageOption={onClickDeleteMessageOption}
                confirmationType={'confirm-delete-post'}
                messageIndex={messageIndex}
                isPostAuthor={isPostAuthor}
                handleChangeUserActionRequired={handleChangeUserActionRequired}
                dsrId={dsrId}
                postId={postId}
                isDsrAdmin={isDsrAdmin}
                postObject={message}
              />
            </FeedPostButtonModule>
          </Flex>


          <Box>
            <Flex align='flex-start' px='0.2em' py={postContent && '1em'} minH={'0.5em'}>
              <Flex className="white-space-preline" width="100%">
                {postType === "shared-url" ?
                  <Box width="100%">
                    <Box fontSize='0.875em'>
                      <MentionsInput
                        readOnly={true}
                        value={postContent}
                      >
                        <Mention
                          markup='@[__display__]userId-(__id__)'
                          style={mentionStyle}
                          displayTransform={(id, display) => { return `@${display}` }}
                        />
                      </MentionsInput>
                    </Box>

                    <Box mt='1em'>
                      <ReactTinyLink
                        cardSize="small"
                        showGraphic={true}
                        width="100%"
                        maxLine={2}
                        minLine={1}
                        url={getUrlInPostContent(postContent)}
                        proxyUrl={corsProxy}
                        onClick={(e) => { openInNewTab(getUrlInPostContent(postContent)) }}
                      />
                    </Box>
                  </Box>

                  :
                  <Box fontSize='0.875em'>
                    <MentionsInput
                      readOnly={true}
                      value={postContent}
                    >
                      <Mention
                        markup='@[__display__]userId-(__id__)'
                        style={mentionStyle}
                        displayTransform={(id, display) => { return `@${display}` }}
                      />
                    </MentionsInput>
                  </Box>}
              </Flex>
            </Flex>
           
            {content && content()}
    
          </Box>

          <Box key={`dsr_replies_${messageIndex}`} my='1em'>
            <DsrRepliesDisplayModule
              messageRepliesArray={postReplies}
              postId={postId}
              dsrTeamMembers={dsrTeamMembers}
              onClickDeleteReplyOption={onClickDeleteReplyOption}
              confirmationType={'confirm-delete-reply'}
              isMobileView={isMobileView}
              isDsrAdmin={isDsrAdmin}
              dsrId={dsrId}
              handleChangeUserActionRequired={handleChangeUserActionRequired}
            />
          </Box>

          <Box mt='1em'>
            <DsrMessageToolbar
              postReactions={postReactions}
              replyCount={replyCount}
              userIdToken={userIdToken}
              userProfile={userProfile}
              messageIndex={messageIndex}
              postId={postId}
              handleAddRemoveMessageReaction={handleAddRemoveMessageReaction}
              handleCreateNewPostReply={handleCreateNewPostReply}
              // Tagging 
              dsrTeamMembers={thisDsrTeamMembers}
              dsrId={dsrId}
              createNewDsrFeedReplyOutcome={createNewDsrFeedReplyOutcome}
              editDsrFeedReplyOutcome={editDsrFeedReplyOutcome}
            />
          </Box>
        </Box>
      )
    }

    const imagesContent = () => {
      const imageStyle = { height: "100%", maxHeight: '400px', width:'100%', objectFit: 'contain', background: '#F7FAFC', padding: "0",  borderRadius: '8px'}

      return (
        <Fragment>
          {images && images.length > 0 && <div>
            {images.length === 1 ?
              images.map((item, index) => {
                if (item.url) {
                  // const displayImage = true
                  const displayImage = acceptedImageFileTypes.some(ext => item.url.toLowerCase().endsWith(ext));
                  if (displayImage) {
                    // URL is for an image
                    return (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt,no-mixed-operators
       
                      <img key={item && item.key || item + index} src={item.url || item} style={imageStyle}
                        draggable="false"
                        alt={`post-image-${index}`} />
               

                    )
                  } 
                  else {
                    // URL is not for a video or an image
                    // eslint-disable-next-line no-mixed-operators
                    return <div key={item && item.key || item + index} />
                  }
                } else {
                  return (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt,no-mixed-operators
                    <img key={item && item.key || item + index} src={item} style={imageStyle}
                      draggable="false"
                      alt={`post-image-${index}`} />
                  )
                }


              }) : <LaunchdeckImageCarousel imageArray={images} />
            }

          </div>}
        </Fragment>
      )
    };

    const videoContent = () => {
      return (
        <Box w='100%' my='2em' py='1em' bg='gray.100' rounded='0.25em'>
          {video && video.length > 0 &&
            <div className="post__images">
              {(video[0].url.endsWith(".mp4") || video[0].url.endsWith(".ogg") || video[0].url.endsWith(".webm"))
                &&
                <video
                  src={video[0].url} controls
                  style={{ width: "auto", height: "25em", objectFit: "contain" }}
                />
              }

            </div>
          }
        </Box>
      )
    };

    const loomVideoContent = () => {
      if (!loomVideo?.sharedUrl) return null

      return <LoomVideoDisplay loomVideo={loomVideo} />
    }

    switch (postType) {
      case ("images"): {
        return (
          <div key={`dsr_replies_${messageIndex}`}>{renderPostTextOrMedia(imagesContent)}</div>
        );
      }
      case ("uploaded-video"): {
        return (
          <div key={`dsr_replies_${messageIndex}`}>{renderPostTextOrMedia(videoContent)}</div>
        );
      }
      case ("short-text"): {
        return (
          <div key={`dsr_replies_${messageIndex}`}>{renderPostTextOrMedia()}</div>
        );
      }
      case ("shared-url"): {
        return (
          <div key={`dsr_replies_${messageIndex}`}>{renderPostTextOrMedia()}</div>
        );
      }
      case ("loom-video-post"): {
        return (
          <div key={`dsr_replies_${messageIndex}`}>{renderPostTextOrMedia(loomVideoContent)}</div>
        );
      }
      case ("dsr-created"): {
        return (
          <Flex flexDirection={['column', 'column', 'row']} key={`message_${messageIndex}`} fontSize='0.9em' p='1em' w='100%' bg='white' rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)' align='center' >
            <Image src={DsrStartIcon} />
            <Box pl='1em' fontWeight='semibold'> {memberName}</Box>
            <Box pl='0.3em'> created </Box>
            <Box textAlign='center' pl='0.5em' color='brand.500' fontWeight='semibold'> {dsrTitle}</Box>
            <Box pl='0.5em' fontWeight='400' lineHeight='1.25em' fontSize='0.75em' color='gray.500'> • {moment(postCreatedAt).format('lll')}</Box>
          </Flex>

        )
      }
      case ("team-changes"): {
        const { changeType, membersChanged, postId } = message || {};

        const displayActionText = (changeType === 'add-member') ? 'added new members to the team' : (changeType === 'remove-member') ? 'removed members from the team' : (changeType === 're-add-member') ? 'added members back to the team' : "posted"

        const memberCards = membersChanged?.map((member, memberIndex) => {
          const { userId } = member || {};
          const addedMemberProfile = getUserDetailsUsingDsrMembersArray(dsrTeamMembers, userId);

          const { email, memberName, memberProfilePicSrc, memberOrgTitle, memberOrgName } = addedMemberProfile || {}
          const supplementaryUserInfo = (memberOrgTitle && memberOrgName) ? `${memberOrgTitle}, ${memberOrgName}` : memberOrgName ? memberOrgName : email;
          return (
            <React.Fragment key={`team_changes_${changeType}_${postId}${memberIndex}`}>
              <Flex justify='flex-start' align='center' mt='1em'>
                <Avatar h='4.2em' w='4.2em' name={memberName} bg='gray.100' color='gray.700' src={memberProfilePicSrc} />
                <Box pl='1em'>
                  <Box fontWeight='400' fontSize='0.75em' lineHeight='1.25em' color='brandDark.500' className='truncate-overflow-text' maxW='25em'>{memberName ? memberName : email}</Box>
                  <Box fontWeight='500' fontSize='0.7em' lineHeight='1em' color='gray.500' mt='0.2em' className='truncate-overflow-text' maxW='25em'>{supplementaryUserInfo}</Box>
                </Box>
              </Flex>
            </React.Fragment>
          )
        })

        return (
          <Box key={`message_${messageIndex}`} p='1em' mb='2em' w='100%' bg='white' rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)' >
            <Flex align='center'>
              <Image src={DsrStartIcon} />
              <Box pl='1em'>
                <Box fontSize='0.9em'>
                  <span style={{ fontWeight: 800 }}
                  >{memberName}</span>{" "}
                  <span>{displayActionText}</span>
                </Box>

                <Box fontSize='0.8em' color='gray.500'>{moment(postCreatedAt).format('lll')}</Box>
              </Box>
            </Flex>

            <Box>
              {memberCards}
            </Box>
          </Box>
        )
      }
      case ("faq-question-post"): {
        const { questionTextContent } = message || {};

        return (
          <Box
            key={`message_${messageIndex}`}
            w='100%'
            mt='1em' mb='2em' p='1em'
            bg='white'
            rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)'
          >
            <Flex width='100%' justify='space-between'>
              <Box>
                <Flex justify='flex-start' align='center'>
                  <Avatar h='4.2em' w='4.2em' name={memberName} bg='gray.100' color='gray.700' src={memberProfilePicSrc} />
                  <Box pl='1em'>
                    <Flex justify='flex-start' fontSize='0.9em'>
                      <Box ><strong>{memberName}</strong> <span>posted a question</span></Box>
                    </Flex>
                    <Box fontSize='0.8em' color='gray.500'>{messageTimestamp}  {isEdited ? <Box as='span' fontSize='1em' color='gray.500' lineHeight='1em' >  •  Edited</Box> : null}</Box>
                  </Box>
                </Flex>
              </Box>
            </Flex>

            <Box>
              <Flex align='flex-start' px='0.2em' py='1em'>
                <Box w='100%' bg='gray.100' rounded='0.25em' p='0.5em'>
                  <Flex className="white-space-preline">
                    <Box fontSize='0.875em' lineHeight='1.5em'>{questionTextContent}</Box>
                  </Flex>
                </Box>
              </Flex>

              <Box fontSize='0.8em' color='brand.500' mt='1em' cursor='pointer' px='0.5em'
                onClick={() => handleSelectSubpage("", "faq-subpage")}
              >See all Q&A questions</Box>



            </Box>


            <Box key={`dsr_replies_${messageIndex}`}>
              <DsrRepliesDisplayModule
                messageRepliesArray={postReplies}
                postId={postId}
                dsrTeamMembers={dsrTeamMembers}
                onClickDeleteReplyOption={onClickDeleteReplyOption}
                confirmationType={'confirm-delete-reply'}
                isMobileView={isMobileView}
                isDsrAdmin={isDsrAdmin}
                dsrId={dsrId}
                handleChangeUserActionRequired={handleChangeUserActionRequired}
              />
            </Box>

            <Box mt='1em'>
              <DsrMessageToolbar
                postReactions={postReactions}
                userIdToken={userIdToken}
                messageIndex={messageIndex}
                postId={postId}
                handleAddRemoveMessageReaction={handleAddRemoveMessageReaction}
                handleCreateNewPostReply={handleCreateNewPostReply}
                // Tagging 
                dsrTeamMembers={thisDsrTeamMembers}
                dsrId={dsrId}
              />
            </Box>

          </Box>
        );
      }
      case ("faq-answer-post"): {
        const { questionTextContent, answerTextContent } = message || {};

        return (
          <Box
            key={`message_${messageIndex}`}
            w='100%'
            mt='1em' mb='2em' p='1em'
            bg='white'
            rounded='0.25em' border='1px solid rgba(203, 213, 224, 0.5)'
          >
            <Flex width='100%' justify='space-between'>
              <Box>
                <Flex justify='flex-start' align='center'>
                  <Avatar h='4.2em' w='4.2em' name={memberName} bg='gray.100' color='gray.700' src={memberProfilePicSrc} />
                  <Box pl='1em'>
                    <Box fontSize='0.9em'>
                      <strong>{memberName}</strong> answered a question
                    </Box>
                    <Box fontSize='0.8em' color='gray.500'>{messageTimestamp}  {isEdited ? <Box as='span' fontSize='1em' color='gray.500' lineHeight='1em' >  •  Edited</Box> : null}</Box>
                  </Box>
                </Flex>
              </Box>

            </Flex>

            <Box>
              <Flex direction='column' align='flex-start' px='0.2em' py='1em'>
                <Box w='100%' rounded='0.25em' p='0.5em'>
                  <Flex className="white-space-preline">
                    <Box fontSize='0.875em' lineHeight='1.5em'>{questionTextContent}</Box>
                  </Flex>
                </Box>
                <Box w='100%' bg='gray.100' rounded='0.25em' p='0.5em' mt='1.2em'>
                  <Flex className="white-space-preline">
                    <Box fontSize='0.875em' lineHeight='1.5em'>{answerTextContent}</Box>
                  </Flex>
                </Box>

              </Flex>

              <Box fontSize='0.8em' color='brand.500' mt='1em' cursor='pointer' px='0.5em'
                onClick={() => handleSelectSubpage("","faq-subpage")}
              >See all Q&A questions</Box>



            </Box>

            <Box key={`dsr_replies_${messageIndex}`}>
              <DsrRepliesDisplayModule
                messageRepliesArray={postReplies}
                postId={postId}
                dsrTeamMembers={dsrTeamMembers}
                onClickDeleteReplyOption={onClickDeleteReplyOption}
                confirmationType={'confirm-delete-reply'}
                isMobileView={isMobileView}
                isDsrAdmin={isDsrAdmin}
                dsrId={dsrId}
                handleChangeUserActionRequired={handleChangeUserActionRequired}
              />
            </Box>

            <Box mt='1em'>
              <DsrMessageToolbar
                postReactions={postReactions}
                userIdToken={userIdToken}
                messageIndex={messageIndex}
                postId={postId}
                handleAddRemoveMessageReaction={handleAddRemoveMessageReaction}
                handleCreateNewPostReply={handleCreateNewPostReply}
                // Tagging 
                dsrTeamMembers={thisDsrTeamMembers}
                dsrId={dsrId}
              />
            </Box>

          </Box>
        );
      }
      default: {
        console.log('There are post types that cannot be rendered:', postType)
        return null
      }
    }
  }
  );

  return (
    <Box>
      <Flex ref={scrollToRef} direction='column' w='100%' mt='1em'>

        <DsrCreateContentPostModule
          handleCreateNewPost={handleCreateNewPost}
          userProfile={userProfile}
          handleOpenNewPostModal={handleOpenNewPostModal}
          handleSelectSubpage={handleSelectSubpage}
          processingNewDsrPosts={processingNewDsrPosts}
        />

        <Box>
          <NewFeedPostsLoadingDisplay processingNewDsrPosts={processingNewDsrPosts}/>

          {displayedPostContent.length < 1
            ? (<LoaderCentered size='5em' />)
            : (displayedMessages)
          }
        </Box>

        {showLoadMore &&
          <Box display="flex" justifyContent="center" mt="1.5em" pb='2em'>
            <LaunchdeckButton bg='brand.500' color='white'
              loadingLogic={loadingMoreContent}
              onClick={loadMoreFeedContent}
              label='More posts' />
          </Box>}
      </Flex>
    </Box>
  )

}


function mapStateToProps(state) {
  return {
    createNewDsrFeedPostOutcome: state.createNewDsrFeedPostOutcome.results.data,
    editDsrFeedPostOutcome: state.editDsrFeedPostOutcome.results.data,
    deleteDsrFeedPostOutcome: state.deleteDsrFeedPostOutcome.results.data,
    createNewDsrFeedReplyOutcome: state.createNewDsrFeedReplyOutcome.results.data,
    editDsrFeedReplyOutcome: state.editDsrFeedReplyOutcome.results.data,
    deleteDsrFeedReplyOutcome: state.deleteDsrFeedReplyOutcome.results.data,
    editDsrReactionOutcome: state.editDsrReactionOutcome.results.data,
    dsrFeedContent: state.dsrFeedContent.results,
    isMobileView: state.mobileViewMode.isMobileView,
    isDsrAdmin: state.dsrAdmin.isDsrAdmin,
    processingDsrPosts: state.processingDsrPosts.results,
    processingNewDsrPosts: state.processingNewDsrPosts.results
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      // Post management
      resetCreateNewDsrFeedPost,
      resetEditDsrFeedPost,
      resetDeleteDsrFeedPost,

      // Reply management
      resetCreateDsrReply,
      resetEditDsrReply,
      resetDeleteDsrReply,

      // Reaction management
      resetEditDsrReaction,
      replaceDsrFeedContent,
    },
    dispatch,
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DisplayNewsFeed));
